"use client";

/**
 * The parts of `chrome.runtime` the web app talks to the extension through.
 * `sendMessage` may return a Promise as well as calling the callback: WebKit
 * browsers' shims (DuckDuckGo, Safari) return one and reject it.
 */
export interface ExtensionRuntimeLike {
  lastError?: { message?: string };
  sendMessage?: (
    extensionId: string,
    message: object,
    callback: (response?: unknown) => void
  ) => unknown;
}

/** The extension answered (`response`), or it is not reachable. */
export type ExtensionReply = { ok: true; response: unknown } | { ok: false };

/** How long a message may go unanswered before it counts as unreachable. */
export const EXTENSION_REPLY_TIMEOUT_MS = 3000;

function isThenable(value: unknown): value is PromiseLike<unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { then?: unknown }).then === "function"
  );
}

/**
 * Sends one message to the extension and settles exactly once
 * (THE-SUKHA-COMPANY-TK, TX-5557): the callback's answer (unless
 * `lastError` is set), a returned Promise's answer, or "not reachable" for a
 * throw, a rejection, `lastError`, or no answer within
 * `EXTENSION_REPLY_TIMEOUT_MS`. A WebKit shim's rejected Promise is always
 * handled here, so it never escapes as an unhandled rejection, and nothing
 * waits forever on a callback that never comes.
 *
 * @param runtime - `window.chrome.runtime`, or a test's stand-in
 * @param extensionId - The extension to ask
 * @param message - What to send
 * @returns The reply, or `{ ok: false }` when the extension is not reachable
 */
export function sendToExtension(
  runtime: ExtensionRuntimeLike,
  extensionId: string,
  message: object
): Promise<ExtensionReply> {
  return new Promise((resolve) => {
    let settled = false;
    const settle = (reply: ExtensionReply) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(reply);
    };
    const timer = setTimeout(() => settle({ ok: false }), EXTENSION_REPLY_TIMEOUT_MS);
    try {
      const returned = runtime.sendMessage?.(extensionId, message, (response?: unknown) => {
        settle(runtime.lastError ? { ok: false } : { ok: true, response });
      });
      if (isThenable(returned)) {
        returned.then(
          (response) => settle({ ok: true, response }),
          () => settle({ ok: false })
        );
      } else if (!runtime.sendMessage) {
        settle({ ok: false });
      }
    } catch {
      settle({ ok: false });
    }
  });
}
